import React, { useEffect, useState } from "react";
import APIEndPoints from "../../utils/APIEndPoints";
import SETTINGS from "../../globals";

interface OAuthTokenResponse {
  access_token: string;
  expires_in: number;
  id_token: string;
  scope: string;
  token_type: string;
}

const CLIENT_ID = process.env.REACT_APP_OAUTH_KEY!;

// Module-level flag so React StrictMode's unmount/remount cycle doesn't
// trigger a second code exchange (authorization codes are single-use).
let callbackProcessed = false;

export default function OAuthCallback({ onAuthenticated }: any) {
  const [status, setStatus] = useState("Processing login...");

  useEffect(() => {
    if (callbackProcessed) return;
    callbackProcessed = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");

    if (error) {
      setStatus(`OAuth error: ${error}`);
      return;
    }

    const expectedState = sessionStorage.getItem("oauth_state");
    if (!state || state !== expectedState) {
      setStatus("Invalid state parameter. Possible CSRF attack.");
      return;
    }

    if (!code) {
      setStatus("No authorization code received.");
      return;
    }

    const codeVerifier = sessionStorage.getItem("oauth_code_verifier");
    if (!codeVerifier) {
      setStatus("Missing PKCE code_verifier.");
      return;
    }

    sessionStorage.removeItem("oauth_state");
    sessionStorage.removeItem("oauth_code_verifier");

    const redirectUri = process.env.REACT_APP_OAUTH_RETURN_URL!;
    //window.location.origin + "/auth/callback";

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: CLIENT_ID,
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
    });

    setStatus("Exchanging code for token...");

    fetch(SETTINGS.API_ROOT + APIEndPoints.OAUTH.TOKEN, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    })
      .then((res) => {
        if (!res.ok) {
          return res.text().then((text) => {
            throw new Error(`Token endpoint returned ${res.status}: ${text}`);
          });
        }
        return res.json();
      })
      .then((data: OAuthTokenResponse) => {
        if (!data.access_token) {
          setStatus("Token response received but access_token was empty.");
          return;
        }
        onAuthenticated(data.access_token);
      })
      .catch((err: Error) => {
        setStatus(`Sign-in failed: ${err.message}`);
      });
  }, []);

  return (
    <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
      <p className="text-gray-700 dark:text-gray-300">{status}</p>
    </div>
  );
}
